import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/**
 * The first read of the vendor's business.
 *
 * A run is started once and then polled: the work is a handful of paid calls
 * that take tens of seconds together, and the vendor is invited to walk away
 * from the screen while it happens. See [[use-vendor-profile-api.hook]] — the
 * domain analysed is whatever the profile has saved, never a value passed
 * from here, so the setup gaps and the run can never disagree.
 */

export type AnalysisStepKind = 'screenshot' | 'overview' | 'keywords' | 'competitors' | 'backlinks';
export type AnalysisStepStatus = 'pending' | 'running' | 'done' | 'skipped' | 'failed';
export type AnalysisStatus = 'pending' | 'running' | 'succeeded' | 'failed';

export interface AnalysisStep {
    kind: AnalysisStepKind;
    status: AnalysisStepStatus;
    /** What the step found, in one line, written by the server. */
    note?: string;
    /** The same note as a key and raw numbers — see `stepNote`. */
    noteI18n?: { key: string; params?: Record<string, number> };
    error?: string;
    /** True when this month's data already existed and nothing was bought. */
    cached?: boolean;
    cost?: number;
    finishedAt?: string;
}

export interface AnalysisRun {
    runId: string;
    target: string;
    status: AnalysisStatus;
    steps: AnalysisStep[];
    summary: Record<string, any>;
    error?: string;
    cost: number;
    finishedAt?: string;
    createdAt: string;
}

const BASE = '/services/pms/vendors/me/analysis';

export const useVendorAnalysisApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    /** Starts a run, or joins the one already going for this vendor. */
    const start = async (opts: { market?: string; force?: boolean } = {}): Promise<AnalysisRun> =>
        (await sendAuthGuardedRequest(ApiMethod.POST, BASE, opts)) as AnalysisRun;

    const get = async (runId: string): Promise<AnalysisRun> =>
        (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/${runId}`)) as AnalysisRun;

    /** Null when this vendor has never run one — an ordinary answer, not an error. */
    const latest = async (): Promise<AnalysisRun | null> =>
        ((await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/latest`)) ?? null) as AnalysisRun | null;

    return { start, get, latest };
};

/**
 * What a finished analysis step says, in the vendor's language.
 *
 * The numbers arrive raw and are formatted here, because "184,881" is English
 * typography — French writes 184 881. Formatting them on the server would have
 * baked a comma into every language, which is how this read in French before.
 *
 * The server's English sentence stays the fallback: a run recorded before this
 * existed has no key, and must still say something.
 */
export function stepNote(
    t: (key: string, opts?: Record<string, unknown>) => string,
    locale: string,
    step: { note?: string; noteI18n?: { key: string; params?: Record<string, number> } },
): string | undefined {
    if (!step.noteI18n?.key) return step.note;

    const number = new Intl.NumberFormat(locale);
    const params = Object.fromEntries(
        Object.entries(step.noteI18n.params ?? {}).map(([k, v]) => [k, number.format(v)]),
    );

    return t(step.noteI18n.key, { ...params, defaultValue: step.note ?? '' });
}
